import styled from 'styled-components';
import { Button, Heading, Modal, Paragraph } from '../Ant';
import { Flex } from 'antd';
import {
  MODAL_CANCEL_BUTTON_MAX_WIDTH,
  MODAL_CONFIRM_BUTTON_MAX_WIDTH,
} from '../../constants/constants';

interface ConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  cancelText: string;
  confirmText: string;
  onConfirm: () => void;
  middleButtonText?: string;
  onMiddleButtonClick?: () => void;
  type?: 'primary' | 'danger';
}

const ModalIcon = styled.div<{ $type?: 'primary' | 'danger' }>`
  width: 70px;
  height: 70px;
  border-radius: 60px;
  background-color: ${({ $type }) =>
    $type === 'primary'
      ? 'rgba(var(--clr-general-primary-rgb), 0.1)'
      : 'rgba(var(--clr-danger-bg-rgb), 0.1)'};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  .icon {
    font-size: 30px;
    color: ${({ $type }) =>
      $type === 'primary' ? 'var(--clr-title)' : 'var(--clr-primary-error)'};
    height: 30px;
    display: inline-block;
  }
`;

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  visible,
  onClose,
  icon,
  title,
  subtitle,
  cancelText,
  confirmText,
  onConfirm,
  onMiddleButtonClick,
  middleButtonText,
  type = 'danger', // Default type is danger
}) => {
  const isStackedLayout = !!middleButtonText;

  return (
    <Modal
      className="confirm-modal"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      closeIcon={null}
      data-testid="confirmation-modal"
      width={370}
    >
      <Flex vertical align="center" className="h-full" gap={15}>
        <ModalIcon $type={type}>{icon}</ModalIcon>
        <Flex vertical gap={5}>
          <Heading
            level={6}
            $fontsize="14px"
            $color="title"
            $weight={600}
            $lineheight="21px"
            className="ta-center"
          >
            {title}
          </Heading>
          {subtitle && (
            <Paragraph $fontsize="12px" className="ta-center" $color="subtitle" $lineheight="20px">
              {subtitle}
            </Paragraph>
          )}
        </Flex>
        <Flex
          gap={isStackedLayout ? 10 : 5}
          align="center"
          className="w-full"
          vertical={isStackedLayout}
        >
          {middleButtonText ? (
            <>
              <Button
                data-testid="confirm-button"
                size="large"
                type={type === 'primary' ? 'primary' : 'default'}
                danger={type === 'danger'}
                onClick={onConfirm}
                block
                className="p-0"
                maxwidth="100%"
              >
                {confirmText}
              </Button>
              {onMiddleButtonClick && (
                <Button
                  data-testid="middle-button"
                  size="large"
                  onClick={onMiddleButtonClick}
                  block
                  maxwidth="100%"
                >
                  {middleButtonText}
                </Button>
              )}
              <Button
                data-testid="cancel-button"
                size="large"
                onClick={onClose}
                block
                maxwidth="100%"
              >
                {cancelText}
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="cancel-button"
                size="large"
                onClick={onClose}
                block
                maxwidth={MODAL_CANCEL_BUTTON_MAX_WIDTH}
              >
                {cancelText}
              </Button>
              <Button
                data-testid="confirm-button"
                size="large"
                type={type === 'primary' ? 'primary' : 'default'}
                danger={type === 'danger'}
                onClick={onConfirm}
                block
                className="p-0"
                maxwidth={MODAL_CONFIRM_BUTTON_MAX_WIDTH}
              >
                {confirmText}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ConfirmModal;
